// utils/creditScoreCalculator.js
import moment from "moment";

const CREDIT_SCORE_WEIGHTS = {
  paymentHistory: 0.35,
  creditUtilization: 0.3,
  loanHistoryLength: 0.15,
  newCredit: 0.1,
  loanDiversity: 0.1,
};

const MAX_SCORE = 850;
const MIN_SCORE = 300;

const calculateCreditScore = (borrower, loans) => {
  try {
    let score = 650; // Base score

    // 1. Payment History Analysis
    const paymentHistoryImpact = analyzePaymentHistory(loans);
    score += paymentHistoryImpact;

    // 2. Credit Utilization
    const utilizationImpact = calculateUtilizationImpact(borrower, loans);
    score += utilizationImpact;

    // 3. Loan History Length
    const historyLengthImpact = calculateHistoryLength(borrower);
    score += historyLengthImpact;

    // 4. New Credit
    const newCreditImpact = analyzeNewCredit(loans);
    score += newCreditImpact;

    // 5. Loan Diversity
    const diversityImpact = analyzeLoanDiversity(loans);
    score += diversityImpact;

    // Ensure score stays within bounds
    return Math.min(Math.max(Math.round(score), MIN_SCORE), MAX_SCORE);
  } catch (error) {
    console.error("Credit calculation error:", error);
    return MIN_SCORE; // Fallback score
  }
};

// Helper functions
const analyzePaymentHistory = (loans) => {
  let onTimePayments = 0;
  let latePayments = 0;
  let severeIssues = 0;

  loans.forEach((loan) => {
    loan.transactions?.forEach((transaction) => {
      if (transaction.transaction_type === "Penalty Fee") latePayments++;
      if (transaction.transaction_status === "Failed") severeIssues++;
    });

    if (loan.loan_status === 8) {
      // Overdue status
      const overdueDays = moment().diff(moment(loan.due_date), "days");
      latePayments += Math.min(Math.floor(overdueDays / 30), 3);
    }
  });

  const paymentRatio = latePayments > 0 ? onTimePayments / latePayments : 1;
  return (
    paymentRatio * CREDIT_SCORE_WEIGHTS.paymentHistory * 100 - severeIssues * 10
  );
};

const calculateUtilizationImpact = (borrower, loans) => {
  const totalCredit = loans.reduce((sum, loan) => sum + loan.amount, 0);
  const creditUsed = loans.reduce(
    (sum, loan) => sum + (loan.paid_amount || 0),
    0
  );
  const utilizationRatio = creditUsed / totalCredit || 0;

  return (
    (utilizationRatio > 0.3 ? -50 : 30) * CREDIT_SCORE_WEIGHTS.creditUtilization
  );
};

const calculateHistoryLength = (borrower) => {
  const memberSinceYears = moment().diff(
    moment(borrower.date_created),
    "years"
  );
  return (
    Math.min(memberSinceYears * 10, 50) * CREDIT_SCORE_WEIGHTS.loanHistoryLength
  );
};

const analyzeNewCredit = (loans) => {
  const recentLoans = loans.filter(
    (loan) => moment().diff(moment(loan.date_created), "months") < 6
  );
  return recentLoans.length > 2 ? -30 * CREDIT_SCORE_WEIGHTS.newCredit : 15;
};

const analyzeLoanDiversity = (loans) => {
  const loanTypes = new Set(loans.map((loan) => loan.loan_type));
  return loanTypes.size * 15 * CREDIT_SCORE_WEIGHTS.loanDiversity;
};

export default calculateCreditScore;
